import { jsPDF } from "jspdf";
import JSZip from "jszip";
import QRCode from "qrcode";
import api from "@/lib/api";

export const QR_KEY = "__qr__";

export function qrTextForRow(row, fields) {
  return (fields || [])
    .filter((f) => f.type !== "photo")
    .map((f) => `${f.label}: ${row[f.key] != null ? row[f.key] : ""}`)
    .join("\n");
}

export async function makeQrImage(text, px = 400) {
  const url = await QRCode.toDataURL(text || " ", { width: px, margin: 1, errorCorrectionLevel: "M" });
  return loadImage(url);
}

export const FONTS = [
  "Helvetica", "Arial", "Times New Roman", "Georgia", "Courier New",
  "Poppins", "Roboto", "Plus Jakarta Sans", "Hind",
];

export const FIELD_TYPES = [
  { value: "text", label: "Text" },
  { value: "number", label: "Number" },
  { value: "email", label: "Email" },
  { value: "date", label: "Date" },
  { value: "time", label: "Time" },
  { value: "photo", label: "Photo / JPG" },
];

export const PAGE_SIZES = [
  { value: "a4", label: "A4 (210 × 297 mm)", w: 210, h: 297 },
  { value: "a3", label: "A3 (297 × 420 mm)", w: 297, h: 420 },
  { value: "legal", label: "Legal (8.5 × 14 inch)", w: 215.9, h: 355.6 },
  { value: "12x18", label: "12 × 18 inch", w: 304.8, h: 457.2 },
  { value: "13x19", label: "13 × 19 inch", w: 330.2, h: 482.6 },
];

export function unitToMm(v, unit) {
  return unit === "cm" ? v * 10 : unit === "inch" ? v * 25.4 : v;
}

export function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function drawImageCover(ctx, img, x, y, w, h) {
  const ar = img.width / img.height;
  const box = w / h;
  let sw = img.width, sh = img.height, sx = 0, sy = 0;
  if (ar > box) { sw = img.height * box; sx = (img.width - sw) / 2; }
  else { sh = img.width / box; sy = (img.height - sh) / 2; }
  ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h);
}

function strokeBox(ctx, x, y, w, h) {
  ctx.save();
  ctx.strokeStyle = "#0891b2";
  ctx.lineWidth = 2;
  ctx.setLineDash([6, 4]);
  ctx.strokeRect(x, y, w, h);
  ctx.restore();
}

export function drawCertificate(canvas, tpl, bgImg, layout, values, photoImgs, opts = {}) {
  const W = canvas.width, H = canvas.height;
  const ctx = canvas.getContext("2d");
  ctx.clearRect(0, 0, W, H);
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, W, H);
  if (bgImg) ctx.drawImage(bgImg, 0, 0, W, H);

  for (const f of layout) {
    const x = f.x * W, y = f.y * H;
    if (f.type === "photo") {
      const w = (f.w || 0.2) * W, h = (f.h || 0.25) * H;
      const img = photoImgs && photoImgs[f.key];
      const rad = f.shape === "round" ? 0 : Math.min(f.radius || 0, 0.5) * Math.min(w, h);
      const photoPath = (inset = 0) => {
        ctx.beginPath();
        if (f.shape === "round") ctx.ellipse(x + w / 2, y + h / 2, Math.max(1, w / 2 - inset), Math.max(1, h / 2 - inset), 0, 0, Math.PI * 2);
        else if (rad > 0) ctx.roundRect(x + inset, y + inset, w - inset * 2, h - inset * 2, Math.max(0, rad - inset));
        else ctx.rect(x + inset, y + inset, w - inset * 2, h - inset * 2);
      };
      ctx.save();
      photoPath(0);
      ctx.clip();
      if (img) drawImageCover(ctx, img, x, y, w, h);
      else {
        ctx.fillStyle = "#e2e8f0"; ctx.fillRect(x, y, w, h);
        ctx.fillStyle = "#94a3b8"; ctx.textAlign = "center"; ctx.textBaseline = "middle";
        ctx.font = `${Math.max(10, 0.04 * W)}px Helvetica`;
        ctx.fillText("PHOTO", x + w / 2, y + h / 2);
      }
      ctx.restore();
      const bw = (f.borderW || 0) * W;
      if (bw > 0) {
        ctx.save();
        ctx.strokeStyle = f.borderColor || "#111827";
        ctx.lineWidth = bw;
        photoPath(bw / 2);
        ctx.stroke();
        ctx.restore();
      }
      if (opts.selectedKey === f.key) strokeBox(ctx, x, y, w, h);
    } else if (f.type === "qr") {
      const w = (f.w || 0.2) * W, h = (f.h || 0.2) * H;
      const img = photoImgs && photoImgs[f.key];
      if (img) drawImageCover(ctx, img, x, y, w, h);
      else {
        ctx.fillStyle = "#f1f5f9"; ctx.fillRect(x, y, w, h);
        ctx.strokeStyle = "#94a3b8"; ctx.lineWidth = 1; ctx.strokeRect(x, y, w, h);
        ctx.fillStyle = "#64748b"; ctx.textAlign = "center"; ctx.textBaseline = "middle";
        ctx.font = `${Math.max(10, 0.04 * W)}px Helvetica`;
        ctx.fillText("QR CODE", x + w / 2, y + h / 2);
      }
      if (opts.selectedKey === f.key) strokeBox(ctx, x, y, w, h);
    } else {
      const fs = (f.fontSize || 0.05) * W;
      ctx.font = `${f.italic ? "italic " : ""}${f.bold ? "bold " : ""}${fs}px "${f.fontFamily || "Helvetica"}"`;
      ctx.fillStyle = f.color || "#111827";
      ctx.textAlign = f.align || "left";
      ctx.textBaseline = "top";
      const bw = (f.w || 0.4) * W;
      const bh = f.h ? f.h * H : fs * 1.3;
      let tx = x;
      if (f.align === "center") tx = x + bw / 2;
      else if (f.align === "right") tx = x + bw;
      let text = values && values[f.key] != null && values[f.key] !== "" ? String(values[f.key]) : "";
      if (!text && opts.placeholders) text = `{${f.label || f.key}}`;
      const lineH = fs * 1.3;
      const words = String(text).split(/\s+/).filter(Boolean);
      ctx.save();
      ctx.beginPath();
      ctx.rect(x, y, bw, bh);
      ctx.clip();
      let line = "", yy = y;
      for (const word of words) {
        const test = line ? line + " " + word : word;
        if (ctx.measureText(test).width > bw && line) {
          ctx.fillText(line, tx, yy);
          yy += lineH;
          line = word;
        } else {
          line = test;
        }
      }
      if (line) ctx.fillText(line, tx, yy);
      ctx.restore();
      if (opts.selectedKey === f.key) strokeBox(ctx, x, y, bw, bh);
    }
  }
}

// Resolve all photo dataURLs for given rows -> {photoId: HTMLImageElement}
export async function preloadRowPhotos(rows, fields) {
  const photoKeys = fields.filter((f) => f.type === "photo").map((f) => f.key);
  const ids = new Set();
  rows.forEach((r) => photoKeys.forEach((k) => { if (r[k]) ids.add(r[k]); }));
  if (ids.size === 0) return {};
  const { data } = await api.post("/idc/photos/batch", { ids: [...ids] });
  const imgs = {};
  await Promise.all(Object.entries(data).map(async ([id, url]) => {
    try { imgs[id] = await loadImage(url); } catch { /* skip */ }
  }));
  return imgs;
}

async function renderRowCanvas(tpl, bgImg, layout, row, photoImgMap, fields) {
  const cw = unitToMm(tpl.width, tpl.unit), ch = unitToMm(tpl.height, tpl.unit);
  const pxPerMm = Math.min(12, 2400 / Math.max(cw, ch));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(cw * pxPerMm);
  canvas.height = Math.round(ch * pxPerMm);
  const photoImgs = {};
  fields.filter((f) => f.type === "photo").forEach((f) => {
    const pid = row[f.key];
    if (pid && photoImgMap[pid]) photoImgs[f.key] = photoImgMap[pid];
  });
  for (const f of layout.filter((l) => l.type === "qr")) {
    try { photoImgs[f.key] = await makeQrImage(qrTextForRow(row, fields)); } catch { /* skip */ }
  }
  drawCertificate(canvas, tpl, bgImg, layout, row, photoImgs);
  return canvas;
}

export async function generatePDF(tpl, layout, rows, fields, filename, pageSize = PAGE_SIZES[0], gapMm = 2) {
  await (document.fonts ? document.fonts.ready : Promise.resolve());
  const bgImg = tpl.image ? await loadImage(tpl.image) : null;
  const photoMap = await preloadRowPhotos(rows, fields);
  const doc = new jsPDF({ unit: "mm", format: [pageSize.w, pageSize.h], orientation: "portrait" });
  const pageW = pageSize.w, pageH = pageSize.h, margin = 3;
  const gap = Number.isFinite(gapMm) ? Math.max(0, gapMm) : 2;
  let cw = unitToMm(tpl.width, tpl.unit), ch = unitToMm(tpl.height, tpl.unit);
  const maxW = pageW - 2 * margin, maxH = pageH - 2 * margin;
  if (cw > maxW || ch > maxH) { const s = Math.min(maxW / cw, maxH / ch); cw *= s; ch *= s; }
  let x = margin, y = margin, first = true;
  for (const row of rows) {
    if (x + cw > pageW - margin + 0.1) { x = margin; y += ch + gap; }
    if (y + ch > pageH - margin + 0.1) { if (!first) doc.addPage(); x = margin; y = margin; }
    const canvas = await renderRowCanvas(tpl, bgImg, layout, row, photoMap, fields);
    doc.addImage(canvas.toDataURL("image/jpeg", 0.92), "JPEG", x, y, cw, ch);
    x += cw + gap;
    first = false;
  }
  doc.save(`${filename || "id-cards"}.pdf`);
}

export async function generateJPGZip(tpl, layout, rows, fields, filename) {
  await (document.fonts ? document.fonts.ready : Promise.resolve());
  const bgImg = tpl.image ? await loadImage(tpl.image) : null;
  const photoMap = await preloadRowPhotos(rows, fields);
  const zip = new JSZip();
  let i = 1;
  for (const row of rows) {
    const canvas = await renderRowCanvas(tpl, bgImg, layout, row, photoMap, fields);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.92));
    zip.file(`${filename || "id"}-${i}.jpg`, blob);
    i++;
  }
  const out = await zip.generateAsync({ type: "blob" });
  const url = URL.createObjectURL(out);
  const a = document.createElement("a");
  a.href = url; a.download = `${filename || "id-cards"}.zip`;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}
